import { accent, secondAccent } from 'mainTheme'
import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * Helper function to draw the selection box based on start and end coordinates
 */
function drawSelectionBox(boxElement, start, end) {
  if (!boxElement || !start || !end) return

  if (end.x > start.x) {
    boxElement.style.left = start.x + 'px'
    boxElement.style.width = end.x - start.x + 'px'
  } else {
    boxElement.style.left = end.x + 'px'
    boxElement.style.width = start.x - end.x + 'px'
  }

  if (end.y > start.y) {
    boxElement.style.top = start.y + 'px'
    boxElement.style.height = end.y - start.y + 'px'
  } else {
    boxElement.style.top = end.y + 'px'
    boxElement.style.height = start.y - end.y + 'px'
  }
}

/**
 * Custom hook for area selection (drag to select)
 * Creates a visual selection box and tracks mouse movements
 */
export const useAreaSelection = ({ containerRef, isEnabled }) => {
  // Create a unique box element per hook instance (not shared across components)
  const boxElementRef = useRef(null)

  // Initialize box element once
  if (!boxElementRef.current) {
    const box = document.createElement('div')
    box.style.position = 'fixed'
    box.style.pointerEvents = 'none'
    box.style.borderRadius = '10px'
    box.style.border = `2px dashed ${secondAccent}`
    box.style.opacity = '0.5'
    box.style.backgroundColor = accent
    box.style.zIndex = '9999'
    boxElementRef.current = box
  }
  const [mouseDown, setMouseDown] = useState(false)
  const [selection, setSelection] = useState(null)
  const [drawArea, setDrawArea] = useState({
    start: undefined,
    end: undefined
  })

  const handleMouseMove = useCallback((e) => {
    document.body.style.userSelect = 'none'
    setDrawArea((prev) => ({
      ...prev,
      end: {
        x: e.clientX,
        y: e.clientY
      }
    }))
  }, [])

  const handleMouseDown = useCallback(
    (e) => {
      const containerElement = containerRef.current

      setMouseDown(true)

      if (containerElement && containerElement.contains(e.target)) {
        document.addEventListener('mousemove', handleMouseMove)
        setDrawArea({
          start: {
            x: e.clientX,
            y: e.clientY
          },
          end: {
            x: e.clientX,
            y: e.clientY
          }
        })
      }
    },
    [containerRef, handleMouseMove]
  )

  const handleMouseUp = useCallback(() => {
    document.body.style.userSelect = 'initial'
    document.removeEventListener('mousemove', handleMouseMove)
    setMouseDown(false)
  }, [handleMouseMove])

  useEffect(() => {
    const containerElement = containerRef.current
    if (containerElement && isEnabled) {
      containerElement.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        containerElement.removeEventListener('mousedown', handleMouseDown)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isEnabled, handleMouseUp, handleMouseDown])

  // Update selection box position and dimensions when draw area changes
  useEffect(() => {
    const { start, end } = drawArea
    const boxElement = boxElementRef.current

    if (start && end && boxElement && isEnabled) {
      drawSelectionBox(boxElement, start, end)
      setSelection(boxElement.getBoundingClientRect())
    }
  }, [drawArea, isEnabled])

  // Manage box element in DOM based on mouse state
  useEffect(() => {
    const containerElement = containerRef.current
    const selectionBoxElement = boxElementRef.current

    if (containerElement && selectionBoxElement) {
      if (mouseDown && isEnabled) {
        if (!document.body.contains(selectionBoxElement)) {
          containerElement.appendChild(selectionBoxElement)
        }
      } else if (containerElement.contains(selectionBoxElement)) {
        containerElement.removeChild(selectionBoxElement)
      }
    }
  }, [mouseDown, containerRef, isEnabled])

  // Cleanup: remove box element and event listeners on unmount
  useEffect(() => {
    const boxElement = boxElementRef.current

    return () => {
      if (boxElement && boxElement.parentNode) {
        boxElement.parentNode.removeChild(boxElement)
      }
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = 'initial'
    }
  }, [handleMouseMove, handleMouseUp])

  return { selection, setSelection, mouseDown }
}
